import { ref } from 'vue';
import axios from 'axios';

/**
 * Lightweight composable for handling document uploads during tutor onboarding
 * Uploads files directly to storage using presigned URLs
 *
 * @param {string} slug - The tutor application slug
 * @param {string} presignedUrlEndpoint - Endpoint to get presigned upload URL
 * @param {string} downloadUrlEndpoint - Endpoint to get download URL
 * @returns {Object} Upload utilities and state
 */
export function useOnboardingDocumentUpload(slug, presignedUrlEndpoint, downloadUrlEndpoint) {
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const uploadError = ref(null);

    /**
     * Upload a document directly to storage (client-side)
     *
     * @param {File} file - The file to upload
     * @param {string} type - Document type (dbs, identity, etc.)
     * @param {Object} options - Additional options (onSuccess, onError)
     * @returns {Promise<Object>} Upload result with path and metadata
     */
    const uploadDocument = async (file, type, options = {}) => {
        isUploading.value = true;
        uploadProgress.value = 0;
        uploadError.value = null;

        try {
            // Step 1: Request a presigned upload URL from the backend
            const signedUrlResponse = await axios.post(presignedUrlEndpoint, {
                filename: file.name,
                type: type,
                content_type: file.type,
            }, {
                headers: {
                    'X-Tutor-Slug': slug,
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                }
            });

            if (!signedUrlResponse.data.success) {
                throw new Error(signedUrlResponse.data.message || 'Failed to generate signed URL');
            }

            const { upload_url, path, filename, content_type } = signedUrlResponse.data.data;

            // Step 2: Upload directly to storage using the presigned URL
            await axios.put(upload_url, file, {
                headers: {
                    'Content-Type': content_type,
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    }
                },
                transformRequest: [(data) => data],
                maxRedirects: 0,
                withCredentials: false,
            });

            // Step 3: Return upload result
            const result = {
                path,
                filename,
                original_name: file.name,
                type,
                size: file.size,
                mime_type: file.type,
            };

            uploadProgress.value = 100;

            if (options.onSuccess) {
                options.onSuccess(result);
            }

            return result;

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
            uploadError.value = errorMessage;

            if (options.onError) {
                options.onError(errorMessage);
            }

            throw new Error(errorMessage);

        } finally {
            isUploading.value = false;
        }
    };

    /**
     * Get a temporary download URL for a document
     *
     * @param {string} path - File path in storage
     * @param {string} type - Document type
     * @returns {Promise<Object>} Download URL data
     */
    const getDownloadUrl = async (path, type) => {
        try {
            const response = await axios.post(downloadUrlEndpoint, {
                path,
                type,
            }, {
                headers: {
                    'X-Tutor-Slug': slug,
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                }
            });

            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to generate download URL');
            }

            return response.data.data;

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to get download URL';
            throw new Error(errorMessage);
        }
    };

    return {
        // State
        isUploading,
        uploadProgress,
        uploadError,

        // Methods
        uploadDocument,
        getDownloadUrl,
    };
}
